import React, { useState, useEffect } from 'react';
import { ArrowRight, Ship, Truck, Clock, Lock, Compass, DollarSign, CheckCircle, Shield } from 'lucide-react';
import SEO from '../components/SEO';
import sms360 from '../lib/sms360';

const Home = () => {
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    sms360.getAvailability()
      .then((data) => setAvailability(data))
      .catch(() => {});
  }, []);
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SelfStorage",
    "name": "VD Storage Bells",
    "description": "Open-air parking for trucks, trailers, fleet vehicles, boats & RVs in Bells, TX. See rates and reserve online. 24/7 gate access, fenced & lighted. Hwy 82 near Lake Texoma.",
    "url": "https://vdstoragebells.com",
    "telephone": "903-648-7100",
    "email": "vandd.bells@gmail.com",
    "image": "https://vdstoragebells.com/logo192.png",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "5346 Hwy 82",
      "addressLocality": "Bells",
      "addressRegion": "TX",
      "postalCode": "75414",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 33.6133,
      "longitude": -96.4181
    },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], "opens": "00:00", "closes": "23:59" }
    ],
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "24-Hour Access", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Electronic Gate Access", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Fenced & Lighted", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Drive-Up Access", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Boat & RV Storage", "value": true }
    ],
    "areaServed": [
      { "@type": "City", "name": "Bells, TX" },
      { "@type": "City", "name": "Sherman, TX" },
      { "@type": "City", "name": "Denison, TX" },
      { "@type": "City", "name": "Bonham, TX" },
      { "@type": "City", "name": "Whitewright, TX" },
      { "@type": "City", "name": "Van Alstyne, TX" },
      { "@type": "City", "name": "Savoy, TX" }
    ],
    "paymentAccepted": "Cash, Visa, Mastercard, American Express, Discover",
    "priceRange": "$"
  };

  const features = [
    {
      icon: <Truck className="h-8 w-8 text-green-600" />,
      title: 'Truck & Fleet Parking',
      description: 'Bobtails, box trucks, work trailers, and fleet vehicles. A legal place to park what won\'t fit at home or the HOA.'
    },
    {
      icon: <Ship className="h-8 w-8 text-green-600" />,
      title: 'Boat & RV Storage',
      description: 'Minutes from Lake Texoma and Bois d\'Arc Lake. Perfect for boats, RVs, and trailers.'
    },
    {
      icon: <Clock className="h-8 w-8 text-green-600" />,
      title: '24/7 Access',
      description: 'Access your vehicle, boat, or equipment anytime. Your schedule, your convenience.'
    },
    {
      icon: <Lock className="h-8 w-8 text-green-600" />,
      title: 'Secure & Gated',
      description: 'Fenced perimeter with secure gate access keeps your property safe and protected.'
    },
    {
      icon: <Compass className="h-8 w-8 text-green-600" />,
      title: 'Easy Highway Access',
      description: 'Located right on Highway 82 for easy in-and-out access with your trailer.'
    },
    {
      icon: <DollarSign className="h-8 w-8 text-green-600" />,
      title: 'Affordable Rates',
      description: 'No-hassle monthly rates. Pay less than covered storage without sacrificing security.'
    }
  ];

  const benefits = [
    'Gravel lots',
    'Wide drive aisles for easy maneuvering',
    'Bobtails, box trucks & fleet vehicles welcome',
    'No size restrictions on boats/RVs',
    'Month-to-month rentals',
    'Fleet & business accounts welcome',
    'See rates online — reserve instantly'
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="VD Storage Bells | Boat, RV & Business Storage in Bells, TX"
        description="Open-air truck, trailer, fleet, boat & RV parking on Hwy 82 in Bells, TX. See rates and reserve online. 24/7 gate access. Serving Sherman, Denison & Grayson County."
        canonical="/"
        keywords="truck parking Bells TX, fleet storage Sherman TX, bobtail parking Grayson County, boat storage Lake Texoma, RV storage near US-75, commercial vehicle parking Bells TX"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section
        className="relative h-[600px] bg-cover bg-center hero-overlay"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600181914037-b14638c1137d)' }}
        data-testid="hero-section"
      >
        <div className="hero-content h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 animate-fadeInUp">
              Truck, Trailer & Fleet Parking
              <br />
              <span className="text-green-200">Boats & RVs Welcome — Bells, TX</span>
            </h1>
            <p className="text-xl md:text-2xl text-white mb-4 max-w-3xl animate-fadeInUp">
              See your rate and reserve online — open-air parking on Highway 82
            </p>
            <p className="text-lg md:text-xl text-green-100 mb-4 max-w-3xl animate-fadeInUp">
              Minutes from Lake Texoma • 24/7 Access • Fleet & Business Accounts
            </p>
            {availability && availability.total_available > 0 && (
              <p className="text-lg font-semibold mb-8 animate-fadeInUp">
                <span className="bg-green-500 text-white px-4 py-2 rounded-full">
                  {availability.total_available} spaces available — starting at ${Math.min(...availability.unit_types.map(u => u.monthly_rate))}/mo
                </span>
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp">
              <a
                href="https://vdstoragebells.mysms360.com/rent?utm_source=vdstoragebells.com&utm_content=hero_reserve"
                onClick={() => {
                  if (window.gtag) {
                    window.gtag('event', 'conversion_intent', {
                      'event_category': 'lead',
                      'event_label': 'hero_see_rates_reserve',
                      'facility_name': 'VD Storage Bells'
                    });
                  }
                }}
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-xl btn-primary"
                data-testid="hero-contact-button"
              >
                See Rates & Reserve
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a
                href="/boat-rv-storage-bells-tx"
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-white hover:text-green-600 transition-all"
                data-testid="hero-learn-button"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              <strong>VD Storage Bells</strong> offers open-air parking for bobtails, box trucks, work trailers,
              fleet vehicles, boats, and RVs in Bells, Texas. Located on Highway 82, we provide
              secure outdoor storage with transparent pricing you can see online — reserve your spot
              in minutes.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Whether you need a legal place to park a work truck, store your fleet overnight,
              or keep your boat between trips to Lake Texoma, we have the space at rates you
              can see right now.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-green-600">VD Storage Bells?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Open-air parking built for trucks, trailers, fleet vehicles, boats & RVs in North Texas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md feature-card"
                data-testid={`feature-card-${index}`}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lake Proximity Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Ship className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Perfect for Lake Life
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Store your boat just minutes from Lake Texoma and Bois d'Arc Lake. Get on the water faster
            without paying marina prices or dealing with HOA restrictions.
          </p>
          <a
            href="/boat-rv-storage-bells-tx"
            className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-all shadow-lg"
            data-testid="boat-storage-link"
          >
            Boat & RV Storage Details
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            What You Get
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3" data-testid={`benefit-${index}`}>
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* People Who Serve discount */}
      <section id="people-who-serve" className="scroll-mt-24 py-20 bg-gray-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6 text-green-500" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Honoring Those Who Serve
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Active &amp; veteran military, first responders, and educators give so much to
            our community. We&rsquo;re grateful &mdash; and we&rsquo;d like to give a little back.
          </p>
          <div className="inline-block bg-green-700 rounded-xl px-10 py-6 mb-6">
            <p className="text-3xl md:text-4xl font-bold text-white mb-2">
              10% Off Every Month
            </p>
            <p className="text-green-200 text-sm tracking-wide">
              Military &bull; Police &bull; Fire &bull; EMS &bull; Educators
            </p>
          </div>
          <p className="text-gray-400 text-sm">
            Just mention your service when you rent online or call us &mdash; we&rsquo;ll
            take care of the rest.
          </p>
        </div>
      </section>

      <section className="py-20 bg-gray-900 text-white" data-testid="cta-section" id="reserve">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Reserve Your Space?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {availability
              ? `${availability.total_available} spaces available — 12x30 and 14x40 storage spaces.`
              : 'Contact us today for pricing and availability on our 12x30 and 14x40 storage spaces.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-lg btn-primary"
              data-testid="cta-contact-button"
            >
              Contact Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/storage-sizes"
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-all shadow-lg"
              data-testid="cta-sizes-button"
            >
              View Sizes & Rates
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;